import { useRelatedContents } from '~/composables/contents/useRelatedContents';
import { useTags } from '~/composables/contents/useTags';
import { useStore } from '~/composables/useStore';
import { DYNAMIC_PARTS_NAME } from '~/constants';
import { isInsidePeriod } from '~/utils';
import { PageError } from '~/utils/error';
import { ArticleType } from '~/enums';

type UnwrapPromise<T> = T extends PromiseLike<infer U> ? U : T;
type FetchContentParts = ReturnType<typeof useContentParts>['fetchContentParts'];
export type ContentParts = UnwrapPromise<ReturnType<FetchContentParts>>;

export const useContentParts = () => {
  const { getRelatedSpecialFeatures, getRelatedMagazines, getRelatedProducers, getRelatedEvents } =
    useRelatedContents();
  const store = useStore();
  const { mappedTags } = useTags();

  /**
   * ダイナミックパーツの中に見出しがある場合、目次の対象とする。表示期間外の場合は目次から外す
   * FIXME: nullではなく空配列に修正してもらえたら!!dynamicParts.items.text_with_option.optionは消して良い
   */
  const filterHeadlines = (contents: any) => {
    return contents?.__dynamic_parts.filter((dynamicParts: any) => {
      // 見出しパーツ以外は除外する
      if (dynamicParts.parts !== DYNAMIC_PARTS_NAME.HEADLINE) return false;
      // 「目次に掲載」にチェックが入ってないものは対象外
      if (
        !dynamicParts.items.text_with_option.option ||
        !dynamicParts.items.text_with_option.option.includes(DYNAMIC_PARTS_NAME.TABLE_OF_CONTENTS)
      )
        return false;
      // 表示設定のレスポンスがない場合は一律目次に表示する
      if (!dynamicParts.items.display_management) return true;
      // 表示フラグfalseの場合は目次に非表示
      if (!dynamicParts.items.display_management.is_display) return false;
      // 表示フラグtrueの場合は期間で判定
      return isInsidePeriod(
        dynamicParts.items.display_management.publish_from,
        dynamicParts.items.display_management.publish_to
      );
    });
  };

  const getHeadlines = async (filteredHeadlines: any[]) => {
    return await filteredHeadlines.reduce((acc: any[], cur: any) => {
      const { headline_style, text_with_option } = cur.items;
      const { text } = text_with_option;

      if (headline_style === 'big') {
        acc.push({
          text,
          middles: [],
        });

        return acc;
      }

      if (!acc[acc.length - 1]) {
        acc.push({
          text: '',
          middles: [],
        });
      }

      acc[acc.length - 1].middles.push({
        text,
      });

      return acc;
    }, []);
  };

  const fetchRelatedContents = async (
    relatedSpecialFeatureIds: number[] = [],
    relatedMagazineIds: number[] = [],
    relatedProducerIds: number[] = [],
    relatedEventIds: number[] = []
  ) => {
    const [relatedSpecialFeatures, relatedMagazinesDetails, relatedProducersDetails, relatedEventsDetails] =
      await Promise.all([
        relatedSpecialFeatureIds.length > 0 ? getRelatedSpecialFeatures(relatedSpecialFeatureIds) ?? [] : [],
        relatedMagazineIds.length > 0 ? getRelatedMagazines(relatedMagazineIds) : undefined,
        relatedProducerIds.length > 0 ? getRelatedProducers(relatedProducerIds) : undefined,
        relatedEventIds.length > 0 ? getRelatedEvents(relatedEventIds) : undefined,
      ]);

    return {
      relatedSpecialFeatures,
      relatedMagazines: relatedMagazinesDetails?.articles ?? [],
      relatedMagazinesCategories: relatedMagazinesDetails?.categories ?? [],
      relatedMagazinesAuthors: relatedMagazinesDetails?.authors ?? [],
      relatedProducers: relatedProducersDetails ?? [],
      relatedEvents: relatedEventsDetails?.events ?? [],
      relatedEventTags: relatedEventsDetails?.tags ?? [],
    };
  };

  const fetchContentParts = async (articleType: ArticleType, permalink: string, previewData: any) => {
    let data;

    if (previewData) {
      data = previewData;
    } else if (articleType === ArticleType.Archives) {
      const { result } = await store.dispatch('modules/content/fetchArchivesDetail', {
        permalink,
      });
      data = result[0];
      if (!data) {
        throw new PageError(404, {});
      }
    } else if (articleType === ArticleType.Producers) {
      const { result } = await store.dispatch('modules/content/fetchProducerDetail', {
        permalink,
      });
      data = result[0]; // 生産者記事データなしの場合はECデータのみ表示する
    }

    if (data) {
      const [headlines, relatedContents, tags] = await Promise.all([
        getHeadlines(filterHeadlines(data.contents)),
        fetchRelatedContents(
          data.contents.related_special_features,
          data.contents.related_magazines,
          data.contents.related_producers,
          data.contents.related_events
        ),
        mappedTags(data.contents.tags, []) ?? [],
      ]);
      return {
        data,
        headlines,
        dynamicParts: data.contents.__dynamic_parts as any[],
        ...relatedContents,
        tags,
      };
    } else {
      return {
        data,
        headlines: [],
        dynamicParts: [],
        relatedSpecialFeatures: [],
        relatedMagazines: [],
        relatedMagazinesCategories: [],
        relatedMagazinesAuthors: [],
        relatedProducers: [],
        relatedEvents: [],
        relatedEventTags: [],
        tags: [],
      };
    }
  };

  return {
    fetchContentParts,
  };
};
